<template>
  <div class="sound-resource-editor" v-loading="loading">
    <el-alert
      title="歌曲信息、封面和正文应放在这里，不要再写进标签或搜索词"
      type="info"
      :closable="false"
      show-icon>
      歌手和专辑用于精确管理；歌词、台词只做低优先级正文搜索，不会被拆成一条条搜索词。
    </el-alert>

    <section class="editor-section">
      <div class="section-heading">
        <div>
          <strong>资源基本属性</strong>
          <p>歌手、专辑和封面均为可选信息，只在明确需要时填写并开启展示。</p>
        </div>
      </div>

      <el-row :gutter="16">
        <el-col :span="12">
          <label class="field-label">歌手 / 表演者</label>
          <el-input
            v-model.trim="meta.artist_name"
            maxlength="255"
            clearable
            placeholder="例如：陶喆、蔡依林"
            @input="handleArtistInput" />
        </el-col>
        <el-col :span="12">
          <label class="field-label">专辑 / 作品集</label>
          <el-input
            v-model.trim="meta.album_name"
            maxlength="255"
            clearable
            placeholder="例如：太美丽"
            @input="handleAlbumInput" />
        </el-col>
      </el-row>

      <div class="public-description-field">
        <label class="field-label">公开资源说明</label>
        <el-input
          v-model="meta.public_description"
          type="textarea"
          :rows="4"
          maxlength="1000"
          show-word-limit
          placeholder="填写适合直接展示给用户的资源说明；粗俗但有搜索价值的词请维护到标签别名或独立搜索词"
          @input="markChanged">
        </el-input>
        <p class="field-help">
          此内容会显示在新版客户端资源详情中，不替代旧扩展关键词的兼容搜索职责。
        </p>
      </div>
      <div class="metadata-visibility">
        <div class="visibility-copy">
          <strong>客户端独立信息展示</strong>
          <p>
            客户端始终以音效名称作为主标题。下列内容默认不展示，
            只有人工确认名称和信息已经整理正确后再开启。
          </p>
        </div>
        <div class="visibility-options">
          <div class="visibility-option">
            <div>
              <strong>展示歌手</strong>
              <span>{{ meta.artist_name ? '在标题下方显示歌手' : '请先填写歌手' }}</span>
            </div>
            <el-switch
              v-model="meta.show_artist"
              :active-value="1"
              :inactive-value="0"
              :disabled="!meta.artist_name"
              @change="markChanged" />
          </div>
          <div class="visibility-option">
            <div>
              <strong>展示专辑</strong>
              <span>{{ meta.album_name ? '在标题下方显示专辑' : '请先填写专辑' }}</span>
            </div>
            <el-switch
              v-model="meta.show_album"
              :active-value="1"
              :inactive-value="0"
              :disabled="!meta.album_name"
              @change="markChanged" />
          </div>
        </div>
      </div>
    </section>

    <section class="editor-section cover-section">
      <div class="section-heading">
        <div>
          <strong>资源封面</strong>
          <p>封面从公共图片资源库选择，同一张图片可以被多个业务安全复用。</p>
        </div>
      </div>

      <div class="cover-layout">
        <ImageAssetSelect
          v-model="meta.cover_image_id"
          :selected="meta.cover_asset"
          button-text="选择封面"
          dialog-title="选择音效封面"
          @change="handleCoverAssetChange"
        ></ImageAssetSelect>

        <div class="cover-fields">
          <strong>图片资源绑定</strong>
          <p>
            点击左侧入口可以按名称搜索、按分类筛选并查看大图。图片需先上传到
            “图片资源管理”，这里仅保存资源 ID，不重复上传文件。
          </p>
          <el-alert
            v-if="meta.cover_image_id"
            type="success"
            :closable="false"
            show-icon
            title="已选择图片资源；保存本区域后封面绑定才会正式生效。"
          ></el-alert>
        </div>
      </div>
    </section>

    <section class="editor-section">
      <div class="section-heading">
        <div>
          <strong>歌词、台词与语音正文</strong>
          <p>正文保留原始段落。用户输入其中一句时可以找到资源，但正文命中不会压过名称和标签。</p>
        </div>
      </div>

      <el-tabs v-model="activeContentType" type="border-card">
        <el-tab-pane
          v-for="item in options.content_types"
          :key="item.value"
          :name="item.value"
          :label="item.label">
          <el-row :gutter="16" class="content-toolbar">
            <el-col :span="12">
              <label class="field-label">正文语言</label>
              <el-select
                v-model="contents[item.value].language"
                style="width:100%"
                @change="markChanged">
                <el-option
                  v-for="language in options.languages"
                  :key="language.value"
                  :label="language.label"
                  :value="language.value" />
              </el-select>
            </el-col>
            <el-col :span="12">
              <label class="field-label">允许正文辅助搜索</label>
              <div class="searchable-switch">
                <el-switch
                  v-model="contents[item.value].is_searchable"
                  :active-value="1"
                  :inactive-value="0"
                  @change="markChanged" />
                <span>{{ contents[item.value].is_searchable ? '允许低优先级命中' : '只保存，不参与搜索' }}</span>
              </div>
            </el-col>
          </el-row>
          <el-input
            v-model="contents[item.value].content"
            type="textarea"
            :rows="9"
            maxlength="100000"
            show-word-limit
            :placeholder="contentPlaceholder(item.value)"
            @input="markChanged" />
        </el-tab-pane>
      </el-tabs>
    </section>

    <div v-if="showSaveButton" class="editor-actions">
      <span v-if="!soundId">音效创建成功后自动保存本区域内容</span>
      <span v-else>{{ changed ? '有尚未保存的修改' : '当前内容已同步' }}</span>
      <el-button
        v-if="soundId"
        type="primary"
        icon="el-icon-check"
        :loading="saving"
        @click="save">
        保存资源信息与正文
      </el-button>
    </div>
  </div>
</template>

<script>
import apis from '@/api/index'
import ImageAssetSelect from '@/components/assets/ImageAssetSelect.vue'

export default {
  name: 'SoundResourceContentEditor',
  components: {
    ImageAssetSelect
  },
  props: {
    soundId: {
      type: [Number, String],
      default: null
    },
    showSaveButton: {
      type: Boolean,
      default: true
    }
  },
  /**
   * 创建编辑器的独立状态。
   *
   * @returns {Object}
   */
  data() {
    return {
      loading: false,
      saving: false,
      changed: false,
      loadSequence: 0,
      activeContentType: 'lyrics',
      options: {
        content_types: [
          { value: 'lyrics', label: '歌词' },
          { value: 'quote', label: '台词 / 语录' },
          { value: 'transcript', label: '语音转写' }
        ],
        languages: [
          { value: 'zh-CN', label: '中文' },
          { value: 'en', label: '英语' },
          { value: 'ja', label: '日语' },
          { value: 'ko', label: '韩语' },
          { value: 'other', label: '其他语言' }
        ]
      },
      meta: {},
      contents: {}
    }
  },
  watch: {
    /**
     * 音效 ID 变化时重新加载对应扩展内容。
     *
     * @returns {void}
     */
    soundId: {
      immediate: true,
      handler() {
        this.loadEditor()
      }
    }
  },
  created() {
    this.resetForm()
  },
  methods: {
    /**
     * 返回没有扩展记录时的元数据默认值。
     *
     * @returns {Object}
     */
    createDefaultMeta() {
      return {
        artist_name: '',
        show_artist: 0,
        album_name: '',
        show_album: 0,
        public_description: '',
        cover_image_id: null,
        cover_asset: null
      }
    },

    /**
     * 返回三类正文的完整表单结构，避免模板访问尚未加载的键。
     *
     * @returns {Object}
     */
    createDefaultContents() {
      return {
        lyrics: { language: 'zh-CN', content: '', is_searchable: 1 },
        quote: { language: 'zh-CN', content: '', is_searchable: 1 },
        transcript: { language: 'zh-CN', content: '', is_searchable: 1 }
      }
    },

    /**
     * 重置当前表单。
     *
     * @returns {void}
     */
    resetForm() {
      this.meta = this.createDefaultMeta()
      this.contents = this.createDefaultContents()
      this.changed = false
    },

    /**
     * 加载选项以及当前音效数据。
     *
     * sequence 用于丢弃快速切换音效时较早请求的迟到响应，防止把上一条
     * 音效内容覆盖到当前表单。
     *
     * @returns {Promise<void>}
     */
    async loadEditor() {
      const sequence = ++this.loadSequence
      this.loading = true
      this.resetForm()
      try {
        const requests = [apis.soundResource.options()]
        if (Number(this.soundId) > 0) {
          requests.push(apis.soundResource.read({ sound_id: Number(this.soundId) }))
        }
        const responses = await Promise.all(requests)
        if (sequence !== this.loadSequence) return

        const optionResponse = responses[0]
        if (optionResponse.data.code !== 1) {
          throw new Error(optionResponse.data.msg || '资源选项加载失败')
        }
        this.options = optionResponse.data.data || this.options

        if (responses[1]) {
          if (responses[1].data.code !== 1) {
            throw new Error(responses[1].data.msg || '资源信息加载失败')
          }
          const detail = responses[1].data.data || {}
          this.applyServerData(detail)
        }
        this.changed = false
      } catch (error) {
        this.$message.error(String(error.message || error || '资源信息加载失败'))
      } finally {
        if (sequence === this.loadSequence) this.loading = false
      }
    },

    /**
     * 把服务端数据合并到完整默认结构中。
     *
     * @param {Object} detail 服务端 read/save 返回的数据
     * @returns {void}
     */
    applyServerData(detail) {
      this.meta = {
        ...this.createDefaultMeta(),
        ...(detail.meta || {})
      }
      this.meta.show_artist = Number(this.meta.show_artist) === 1 ? 1 : 0
      this.meta.show_album = Number(this.meta.show_album) === 1 ? 1 : 0
      const nextContents = this.createDefaultContents()
      Object.keys(nextContents).forEach(type => {
        nextContents[type] = {
          ...nextContents[type],
          ...((detail.contents || {})[type] || {})
        }
        nextContents[type].is_searchable = Number(
          nextContents[type].is_searchable
        ) === 1 ? 1 : 0
      })
      this.contents = nextContents
    },

    /**
     * 标记表单存在尚未保存的用户修改。
     *
     * @returns {void}
     */
    markChanged() {
      this.changed = true
    },

    /**
     * 歌手被清空时同步关闭公开开关，避免保存无内容的展示配置。
     *
     * @returns {void}
     */
    handleArtistInput() {
      if (!this.meta.artist_name) this.meta.show_artist = 0
      this.markChanged()
    },

    /**
     * 专辑被清空时同步关闭公开开关，避免保存无内容的展示配置。
     *
     * @returns {void}
     */
    handleAlbumInput() {
      if (!this.meta.album_name) this.meta.show_album = 0
      this.markChanged()
    },

    handleCoverAssetChange(item) {
      this.meta.cover_asset = item
      this.markChanged()
    },

    /**
     * 生成后端保存需要的最小数据结构。
     *
     * 2026-09-11：封面只提交稳定图片条目 ID，不提交文件 ID 或路径。
     *
     * @param {number} soundId 音效 ID
     * @returns {Object}
     */
    buildPayload(soundId) {
      const contents = {}
      this.options.content_types.forEach(item => {
        const source = this.contents[item.value] || {}
        contents[item.value] = {
          language: source.language || 'zh-CN',
          content: source.content || '',
          is_searchable: Number(source.is_searchable) === 1 ? 1 : 0
        }
      })
      return {
        sound_id: Number(soundId),
        meta: {
          artist_name: this.meta.artist_name || '',
          show_artist: Number(this.meta.show_artist) === 1 ? 1 : 0,
          album_name: this.meta.album_name || '',
          show_album: Number(this.meta.show_album) === 1 ? 1 : 0,
          public_description: this.meta.public_description || '',
          cover_image_id: this.meta.cover_image_id
            ? Number(this.meta.cover_image_id)
            : null
        },
        contents
      }
    },

    /** 保存现有音效的资源信息和正文。 */
    async save() {
      const id = Number(this.soundId)
      if (!id) return false
      this.saving = true
      try {
        await this.persistDraft(id)
        this.$message.success('资源信息与正文已保存')
        this.$emit('changed', { soundId: id })
        return true
      } catch (error) {
        this.$message.error(String(error.message || error || '资源信息保存失败'))
        return false
      } finally {
        this.saving = false
      }
    },

    /**
     * 保存草稿。封面只保存公共图片资源 ID，不在音效编辑页重复上传文件。
     *
     * @param {number|string} soundId 已创建的音效 ID
     * @returns {Promise<boolean>}
     */
    async persistDraft(soundId) {
      const id = Number(soundId)
      if (!id) throw new Error('新音效 ID 不正确，无法保存资源信息')

      const response = await apis.soundResource.save(this.buildPayload(id))
      if (response.data.code !== 1) {
        throw new Error(response.data.msg || '资源信息保存失败')
      }
      this.applyServerData(response.data.data || {})
      this.changed = false
      return true
    },

    /**
     * 返回不同正文类型对应的输入提示。
     *
     * @param {string} type 正文类型
     * @returns {string}
     */
    contentPlaceholder(type) {
      const placeholders = {
        lyrics: '粘贴完整歌词并保留原来的分段，不需要拆成搜索词。',
        quote: '填写当前音频包含的台词或语录，可按说话顺序分行。',
        transcript: '填写语音转写全文，可保留时间点或说话人信息。'
      }
      return placeholders[type] || '填写正文内容'
    }
  }
}
</script>

<style lang="scss" scoped>
.sound-resource-editor {
  color: #303133;
}

.editor-section {
  margin-top: 16px;
  padding: 18px;
  border: 1px solid #e7ebf3;
  border-radius: 10px;
  background: #fff;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;

  strong {
    font-size: 16px;
  }

  p {
    margin: 6px 0 0;
    color: #8492a6;
    font-size: 13px;
    line-height: 1.6;
  }
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
}

.public-description-field {
  margin: 18px 0 0;

  .field-help {
    margin: 7px 0 0;
    color: #909399;
    font-size: 12px;
    line-height: 1.6;
  }
}
.metadata-visibility {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid #dfe7f3;
  border-radius: 8px;
  background: #f7faff;
}

.visibility-copy {
  min-width: 0;

  strong {
    font-size: 14px;
  }

  p {
    max-width: 560px;
    margin: 5px 0 0;
    color: #8492a6;
    font-size: 12px;
    line-height: 1.6;
  }
}

.visibility-options {
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
}

.visibility-option {
  display: flex;
  min-width: 176px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 12px;
  border-radius: 7px;
  background: #fff;

  > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  strong {
    font-size: 13px;
  }

  span {
    color: #909399;
    font-size: 11px;
  }
}

.cover-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.cover-fields {
  flex: 1;
  min-width: 0;

  > strong {
    font-size: 14px;
  }

  > p {
    max-width: 660px;
    margin: 6px 0 14px;
    color: #8492a6;
    font-size: 12px;
    line-height: 1.7;
  }
}

.legacy-cover {
  display: flex;
  max-width: 560px;
  align-items: center;
  gap: 14px;
  padding: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fafafa;

  .el-image {
    width: 74px;
    height: 74px;
    flex: 0 0 74px;
    border-radius: 6px;
  }

  > div {
    display: flex;
    min-width: 0;
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
  }

  strong {
    font-size: 13px;
  }

  span {
    color: #909399;
    font-size: 12px;
  }
}

.content-toolbar {
  margin-bottom: 14px;
}

.searchable-switch {
  display: flex;
  min-height: 40px;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 13px;
}

.editor-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  color: #909399;
  font-size: 13px;
}

@media (max-width: 1100px) {
  .cover-layout {
    flex-direction: column;
  }

  .metadata-visibility,
  .visibility-options {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
